import startCase from 'lodash/startCase'

import type {
  SubscriptionAddOnCharge,
  SubscriptionBillingSummary,
  SubscriptionCharge,
  SubscriptionChargebeeData,
} from 'types/subscription'

import type { ActiveAddOn } from './add-ons-row'
import type { BillingPeriod } from './billing-period-row'
import type { BillingSummaryDiscount } from './billing-summary-row'
import type { Price } from './price'

type ChargebeeSubscription = NonNullable<SubscriptionChargebeeData['chargebee']>

export const ADMIN_ONLY_MESSAGE =
  'Only admins on your account can make changes to your plan.'

export const PRICING_PAGE_ERROR_MESSAGE =
  'We couldn’t load the pricing page. Refresh the page to try again.'

const SALES_EMAIL = 'sales@disco.ac'

/** Email to sales, naming the account in the subject so sales can tell who is asking. */
export const getSalesMailto = (hostname?: string): string => {
  const subject = hostname
    ? `Subscription enquiry: ${hostname}`
    : 'Subscription enquiry'
  return `mailto:${SALES_EMAIL}?subject=${encodeURIComponent(subject)}`
}

/** An Infinity allowance is unlimited */
export type Usage = { used: number; allowance: number }

export type CardDetails = {
  /** e.g. "Visa", or null when Chargebee doesn't say */
  brand: string | null
  last4: string | null
}

const hasCents = (cents: number): boolean => cents % 100 !== 0

/**
 * Formats a Chargebee amount in its own currency for the given locale, dropping zero cents,
 * e.g. "A$380" for AUD and "$380" for USD in English.
 * Pass `showCents` to keep the cents on whole amounts, so related amounts share one precision.
 */
export const formatBillingAmount = (
  cents: number,
  currencyCode: string | null | undefined,
  locale: string,
  showCents = hasCents(cents),
): string =>
  new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode || 'USD',
    minimumFractionDigits: showCents ? 2 : 0,
  }).format(cents / 100)

/**
 * Formats the Billing summary row: the amount due at the next renewal, its discounts and the
 * amount before them. Falls back to the current billing amount without a summary. Only positive
 * discounts are kept, and the amount before discounts is left out when there are none or it's unknown.
 */
export const getBillingSummary = ({
  billingSummary,
  currentAmountInCents,
  currencyCode,
  locale,
}: {
  billingSummary: SubscriptionBillingSummary | null | undefined
  currentAmountInCents: number
  currencyCode: string | null | undefined
  locale: string
}): {
  amount: string
  amountBeforeDiscounts: string | null
  discounts: BillingSummaryDiscount[]
} => {
  if (!billingSummary) {
    return {
      amount: formatBillingAmount(currentAmountInCents, currencyCode, locale),
      amountBeforeDiscounts: null,
      discounts: [],
    }
  }

  const appliedDiscounts = billingSummary.discounts.filter(
    discount => discount.amount_in_cents > 0,
  )
  const amountBeforeDiscountsInCents =
    appliedDiscounts.length > 0
      ? billingSummary.amount_before_discounts_in_cents
      : null
  // One precision for the whole row, so it doesn't mix "A$380" with "-A$71.50"
  const showCents = [
    billingSummary.amount_due_in_cents,
    amountBeforeDiscountsInCents,
    ...appliedDiscounts.map(discount => discount.amount_in_cents),
  ].some(cents => cents !== null && hasCents(cents))
  const format = (cents: number) =>
    formatBillingAmount(cents, currencyCode, locale, showCents)

  return {
    amount: format(billingSummary.amount_due_in_cents),
    amountBeforeDiscounts:
      amountBeforeDiscountsInCents === null
        ? null
        : format(amountBeforeDiscountsInCents),
    discounts: appliedDiscounts.map(discount => ({
      label: discount.label,
      amount: format(-discount.amount_in_cents),
    })),
  }
}

/** The card on file, or null without one. */
export const getCardDetails = (
  subscription: Pick<
    ChargebeeSubscription,
    'card_type' | 'last_segment_of_card_number'
  >,
): CardDetails | null => {
  const { card_type, last_segment_of_card_number } = subscription
  if (!card_type && !last_segment_of_card_number) return null

  return {
    brand: card_type ? startCase(card_type) : null,
    last4: last_segment_of_card_number || null,
  }
}

const formatCountry = (countryCode: string, locale: string): string => {
  try {
    return (
      new Intl.DisplayNames([locale], { type: 'region' }).of(countryCode) ??
      countryCode
    )
  } catch {
    return countryCode
  }
}

/** Builds the "Billed to" lines: name, email, street and city, then country. */
export const getBilledToLines = (
  subscription: Pick<
    ChargebeeSubscription,
    | 'first_name'
    | 'last_name'
    | 'email'
    | 'address'
    | 'address_2'
    | 'city'
    | 'state'
    | 'zip'
    | 'country'
  >,
  locale: string,
): string[] => {
  const name = [subscription.first_name, subscription.last_name]
    .filter(Boolean)
    .join(' ')
  const street = [subscription.address, subscription.address_2]
    .filter(Boolean)
    .join(', ')
  const locality = [subscription.city, subscription.state, subscription.zip]
    .filter(Boolean)
    .join(' ')
  const streetAndLocality = [street, locality].filter(Boolean).join(', ')

  return [
    name,
    subscription.email,
    streetAndLocality,
    subscription.country && formatCountry(subscription.country, locale),
  ].filter((line): line is string => Boolean(line))
}

const MONTHS_PER_UNIT = { month: 1, year: 12 } as const

const chargeMonths = (charge: SubscriptionCharge): number =>
  charge.billing_period * MONTHS_PER_UNIT[charge.billing_period_unit]

const getPaidCharges = <T extends SubscriptionCharge>(charges: T[]): T[] =>
  charges.filter(charge => charge.amount_in_cents > 0)

// Converts each charge to the same number of months first, so mixed periods add up correctly
const sumInMonths = (charges: SubscriptionCharge[], months: number): number =>
  Math.round(
    charges.reduce(
      (sum, charge) =>
        sum + (charge.amount_in_cents * months) / chargeMonths(charge),
      0,
    ),
  )

// Sums the charges into the period of the first one
const getChargesPrice = (
  charges: SubscriptionCharge[],
  currencyCode: string | null | undefined,
  locale: string,
): Price | null => {
  const paid = getPaidCharges(charges)
  const [first] = paid
  if (!first) return null

  return {
    amount: formatBillingAmount(
      sumInMonths(paid, chargeMonths(first)),
      currencyCode,
      locale,
    ),
    billingPeriod: first.billing_period,
    billingPeriodUnit: first.billing_period_unit,
  }
}

/** The base plan's price, or null when it's free or Chargebee doesn't say. */
export const getPlanPrice = (
  charge: SubscriptionCharge | null | undefined,
  currencyCode: string | null | undefined,
  locale: string,
): Price | null =>
  charge ? getChargesPrice([charge], currencyCode, locale) : null

/** Totals the add-on charges in the plan's billing period. */
export const getAddOnsTotal = ({
  charges,
  period,
  currencyCode,
  locale,
}: {
  charges: SubscriptionAddOnCharge[]
  period: BillingPeriod | null
  currencyCode: string | null | undefined
  locale: string
}): Price | null => {
  const paid = getPaidCharges(charges)
  if (!period || !paid.length) return null

  return {
    amount: formatBillingAmount(
      sumInMonths(paid, period === 'annual' ? 12 : 1),
      currencyCode,
      locale,
    ),
    billingPeriod: 1,
    billingPeriodUnit: period === 'annual' ? 'year' : 'month',
  }
}

/** Whether the subscription is charged for the add-on. */
export const hasAddOnCharge = (
  charges: SubscriptionAddOnCharge[],
  addon: SubscriptionAddOnCharge['addon'],
): boolean => charges.some(charge => charge.addon === addon)

/**
 * Builds a line per owned add-on, in display order.
 * Price comes from Chargebee; usage comes from DISCO, which Chargebee doesn't track.
 */
export const getActiveAddOns = ({
  charges,
  currencyCode,
  locale,
  discoverySuiteQuota,
  discoverySuiteUsed,
  hasWatermarking,
  watermarkingQuota,
  watermarkingUsed,
  watermarkingUnlimited,
}: {
  charges: SubscriptionAddOnCharge[]
  currencyCode: string | null | undefined
  locale: string
  discoverySuiteQuota: number
  discoverySuiteUsed: number
  hasWatermarking: boolean
  watermarkingQuota: number
  watermarkingUsed: number
  watermarkingUnlimited: boolean
}): ActiveAddOn[] => {
  const lineFor = (
    addon: SubscriptionAddOnCharge['addon'],
    used: number,
    allowance: number,
  ): ActiveAddOn => ({
    addon,
    usage: allowance > 0 ? { used, allowance } : null,
    price: getChargesPrice(
      charges.filter(charge => charge.addon === addon),
      currencyCode,
      locale,
    ),
  })
  const addOns: ActiveAddOn[] = []

  if (hasAddOnCharge(charges, 'discovery_suite')) {
    addOns.push(
      lineFor('discovery_suite', discoverySuiteUsed, discoverySuiteQuota),
    )
  }
  if (hasWatermarking) {
    addOns.push(
      lineFor(
        'watermarking',
        watermarkingUsed,
        watermarkingUnlimited ? Infinity : watermarkingQuota,
      ),
    )
  }

  return addOns
}
